/**
 * Field renderer for the client-portal questionnaire forms.
 *
 * Maps the JSON field `type` shipped by the backend to the right input
 * control, using the same shadcn primitives that the SDLT / AP1 forms
 * use. Honours `show_if` so conditional fields are hidden until the
 * gate field has the right answer.
 *
 * All fields self-report their value to the parent via `onChange(key,
 * value)` — the parent owns the consolidated `answers` map and handles
 * autosave debouncing.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

function FieldShell({ label, hint, required, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-[11px] text-rose-600">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

function isVisible(field, answers) {
  const gate = field.show_if;
  if (!gate) return true;
  return answers[gate.key] === gate.equals;
}

function AddressInput({ name, value, onChange }) {
  const v = value && typeof value === "object" ? value : {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <Input
        placeholder="Number (house / flat)"
        value={v.house_number || ""}
        onChange={(e) => set("house_number", e.target.value)}
        className="h-9 text-sm sm:col-span-2"
        data-testid={`field-${name}-house-number`}
      />
      <Input
        placeholder="Address line 1"
        value={v.line1 || ""}
        onChange={(e) => set("line1", e.target.value)}
        className="h-9 text-sm sm:col-span-2"
        data-testid={`field-${name}-line1`}
      />
      <Input
        placeholder="Address line 2 (optional)"
        value={v.line2 || ""}
        onChange={(e) => set("line2", e.target.value)}
        className="h-9 text-sm sm:col-span-2"
        data-testid={`field-${name}-line2`}
      />
      <Input
        placeholder="Town / city"
        value={v.town || ""}
        onChange={(e) => set("town", e.target.value)}
        className="h-9 text-sm"
        data-testid={`field-${name}-town`}
      />
      <Input
        placeholder="Postcode"
        value={v.postcode || ""}
        onChange={(e) => set("postcode", e.target.value.toUpperCase())}
        className="h-9 text-sm uppercase"
        data-testid={`field-${name}-postcode`}
      />
    </div>
  );
}

export default function FieldRenderer({ field, value, answers, onChange, error, readOnly = false }) {
  if (!isVisible(field, answers)) return null;
  const set = (v) => onChange(field.key, v);

  let control;
  switch (field.type) {
    case "textarea":
      control = (
        <Textarea
          rows={3}
          value={value ?? ""}
          onChange={(e) => set(e.target.value)}
          className="text-sm"
          disabled={readOnly}
          data-testid={`field-${field.key}`}
        />
      );
      break;
    case "select":
      control = (
        <Select value={value ?? ""} onValueChange={set} disabled={readOnly}>
          <SelectTrigger className="h-9 text-sm" data-testid={`field-${field.key}`}>
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;
    case "radio":
      control = (
        <div className="flex flex-wrap gap-2" data-testid={`field-${field.key}`}>
          {(field.options || []).map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                disabled={readOnly}
                onClick={() => set(opt)}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
                  selected
                    ? "bg-[#1462a0] text-white border-[#1462a0]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                } ${readOnly ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                data-testid={`field-${field.key}-opt-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
      break;
    case "checkbox":
      control = (
        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox
            checked={value === true}
            onCheckedChange={(v) => set(v === true)}
            disabled={readOnly}
            data-testid={`field-${field.key}`}
            className="mt-0.5"
          />
          <span className="text-xs text-slate-700">{field.help || "I confirm the above"}</span>
        </label>
      );
      break;
    case "multiselect":
      control = (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid={`field-${field.key}`}>
          {(field.options || []).map((opt) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(opt);
            return (
              <label key={opt} className="flex items-start gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-slate-50">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => {
                    const next = v === true ? [...arr, opt] : arr.filter((x) => x !== opt);
                    set(next);
                  }}
                  disabled={readOnly}
                  className="mt-0.5"
                  data-testid={`field-${field.key}-opt-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                />
                <span className="text-slate-700">{opt}</span>
              </label>
            );
          })}
        </div>
      );
      break;
    case "address":
      control = <AddressInput name={field.key} value={value} onChange={set} />;
      break;
    case "currency":
      control = (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">£</span>
          <Input
            type="number"
            step="0.01"
            value={value ?? ""}
            onChange={(e) => set(e.target.value)}
            className="h-9 text-sm pl-7"
            disabled={readOnly}
            data-testid={`field-${field.key}`}
          />
        </div>
      );
      break;
    case "number":
      control = (
        <Input
          type="number"
          value={value ?? ""}
          onChange={(e) => set(e.target.value)}
          className="h-9 text-sm"
          disabled={readOnly}
          data-testid={`field-${field.key}`}
        />
      );
      break;
    case "email":
      control = (
        <Input
          type="email"
          value={value ?? ""}
          onChange={(e) => set(e.target.value)}
          className="h-9 text-sm"
          disabled={readOnly}
          data-testid={`field-${field.key}`}
        />
      );
      break;
    case "phone":
      control = (
        <Input
          type="tel"
          value={value ?? ""}
          onChange={(e) => set(e.target.value)}
          className="h-9 text-sm"
          disabled={readOnly}
          data-testid={`field-${field.key}`}
        />
      );
      break;
    case "date":
      control = (
        <Input
          type="date"
          value={value ?? ""}
          onChange={(e) => set(e.target.value)}
          className="h-9 text-sm"
          disabled={readOnly}
          data-testid={`field-${field.key}`}
        />
      );
      break;
    default:
      control = (
        <Input
          type="text"
          value={value ?? ""}
          onChange={(e) => set(e.target.value)}
          className="h-9 text-sm"
          disabled={readOnly}
          data-testid={`field-${field.key}`}
        />
      );
  }

  return (
    <FieldShell
      label={field.label}
      hint={field.type !== "checkbox" ? field.help : null}
      required={field.required}
      error={error}
    >
      {control}
    </FieldShell>
  );
}
